// Adaptador de datos para la vista "Recolecciones".
//
// La vista se arma con DOS fuentes que responden preguntas distintas:
//   • routes/routesWithDetails   → la PLANTILLA: qué paradas tiene cada ruta, con su
//     ventana, bultos esperados, montos, geocerca. Es lo que se edita.
//   • recolecciones/ejecucion    → QUÉ PASÓ ese día: qué parada se cumplió y con qué
//     remito, y qué recepciones en bodega hubo.
//
// El segundo endpoint existe desde que `pickups` guarda a qué ruta y a qué parada
// pertenece cada remito. Antes no había tal vínculo y esta vista lo adivinaba acá:
// cruzaba los retiros del día con las paradas por comercio + fecha. Ese cruce atribuía
// mal en dos casos reales — un comercio en varias rutas el mismo día veía su retiro bajo
// la ruta equivocada, y una RECEPCIÓN EN BODEGA (que no pertenece a ninguna ruta)
// terminaba marcando como cumplida la parada de un repartidor que nunca pasó por ahí—.
// Ahora el emparejamiento lo hace el backend por id y acá sólo se indexa.

import { getRoutesWithDetails } from "./routes";
import Axios from "../config/axios-v1-config";

// Frecuencia → días de la semana (0=Dom … 6=Sáb).
// OJO: el backend devuelve `frequency` en formas MEZCLADAS según cómo se creó la
// ruta: "Lunes a viernes"/"weekdays", "Sábado"/"sabado"/"saturday",
// "sunday"/"Domingo", "diario"/"daily". La vista nueva manda `recurrence` y el
// back deriva la `frequency` en INGLÉS ("saturday"/"sunday"). Antes el lookup era
// exacto contra "Sábado" (con acento), así que las rutas guardadas como
// "saturday" quedaban INVISIBLES los sábados. Normalizamos para no depender de la
// forma exacta (la vista vieja ya hacía esto con .toLowerCase()).
const FREQ_TOKEN_DOW = {
  weekdays: [1, 2, 3, 4, 5],
  saturday: [6],
  sunday: [0],
  daily: [0, 1, 2, 3, 4, 5, 6],
};
const FREQ_TOKEN_LABEL = {
  weekdays: "Lunes a viernes",
  saturday: "Sábados",
  sunday: "Domingos",
  daily: "Diario",
};

// frequency (cualquier variante ES/EN, con o sin acento) → token canónico, o "".
export function normalizeFrequency(frequency) {
  const f = (frequency ?? "").toString().toLowerCase().trim();
  if (f === "lunes a viernes" || f === "weekdays" || f === "weekday") return "weekdays";
  if (f === "sábado" || f === "sabado" || f === "saturday") return "saturday";
  if (f === "domingo" || f === "sunday") return "sunday";
  if (f === "diario" || f === "daily") return "daily";
  return ""; // desconocida → no se muestra ningún día (preserva el comportamiento)
}

// Etiqueta legible de la frecuencia, tolerante a la forma del back.
export function frequencyLabel(frequency) {
  return FREQ_TOKEN_LABEL[normalizeFrequency(frequency)] || frequency || "";
}

// Días de la semana (0=Dom … 6=Sáb) en que corre una ruta. FUENTE DE VERDAD:
// `recurrence.daysOfWeek` — el MISMO campo con el que la app del chofer matchea
// en /routes/me. Solo si la ruta legacy no trae `recurrence` caemos al string
// `frequency`. Esto arregla las recurrencias `weekly` de día puntual (ej. lunes)
// y `custom` multi-día: el string `frequency` no sabe representarlas y quedaban
// INVISIBLES en su día (p. ej. una ruta `custom` con lunes no aparecía el lunes).
export function routeDaysOfWeek(route) {
  const dows = route?.recurrence?.daysOfWeek;
  if (Array.isArray(dows) && dows.length) {
    return dows.map(Number).filter((n) => Number.isInteger(n) && n >= 0 && n <= 6);
  }
  // back-compat: aceptar también que nos pasen el string de frecuencia directo.
  const freq = typeof route === "string" ? route : route?.frequency;
  return FREQ_TOKEN_DOW[normalizeFrequency(freq)] || [];
}

export function routeRunsOnDow(route, dow) {
  return routeDaysOfWeek(route).includes(dow);
}

const DOW_SHORT_ES = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

// Etiqueta legible de la recurrencia. Prefiere derivarla de `recurrence.daysOfWeek`
// (cubre weekly/custom); cae al string `frequency` para rutas legacy sin recurrence.
export function recurrenceLabel(route) {
  const raw = route?.recurrence?.daysOfWeek;
  if (Array.isArray(raw) && raw.length) {
    const dows = [...new Set(raw.map(Number).filter((n) => n >= 0 && n <= 6))].sort(
      (a, b) => a - b,
    );
    const key = dows.join(",");
    if (key === "1,2,3,4,5") return FREQ_TOKEN_LABEL.weekdays;
    if (key === "6") return FREQ_TOKEN_LABEL.saturday;
    if (key === "0") return FREQ_TOKEN_LABEL.sunday;
    if (key === "0,1,2,3,4,5,6") return FREQ_TOKEN_LABEL.daily;
    return dows.map((d) => DOW_SHORT_ES[d]).join(", ");
  }
  return frequencyLabel(typeof route === "string" ? route : route?.frequency);
}

// Normaliza un texto para el buscador de la vista (ruta, chofer, comercio, dirección).
// Ya no se usa para emparejar nada: el vínculo retiro↔parada viene resuelto por id.
export function normName(s) {
  return (s || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

// "YYYY-MM-DD" local sin corrimiento de zona
export function ymd(date) {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function parseYmd(s) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}

const EJECUCION_VACIA = { paradas: [], recepciones: [], anuladas: [] };

// Ejecución de UN día. La zona horaria la decide el navegador: el backend recorta el día
// con ella, así que lo que se ve acá coincide con el día que el operador tiene en pantalla
// (una colecta de las 21:00 en Santiago ocurre al día siguiente en UTC).
export async function getEjecucionDelDia(fecha) {
  const params = new URLSearchParams({ fecha });
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (tz) params.append("tz", tz);
  const { data } = await Axios.get(`recolecciones/ejecucion?${params.toString()}`);
  const payload = data?.data ?? data;
  return {
    paradas: Array.isArray(payload?.paradas) ? payload.paradas : [],
    recepciones: Array.isArray(payload?.recepciones) ? payload.recepciones : [],
    // Paradas que el repartidor anuló ese día: llegó y el comercio no tenía pedidos.
    // No traen remito —no se retiró nada—, por eso vienen en su propio conjunto y no
    // dentro de `paradas`.
    anuladas: Array.isArray(payload?.anuladas) ? payload.anuladas : [],
  };
}

// Trae la plantilla de rutas + la ejecución de cada día de la franja.
//
// Una consulta por día porque el endpoint es por día. Son 7 y van en paralelo, así que la
// latencia es la de una sola; si la franja creciera o el volumen lo pidiera, conviene
// darle modo rango al endpoint en vez de multiplicar llamadas.
//
// `getRoutesWithDetails` retorna el wrapper { ok, data } del backend (otros callers
// también desempacan manualmente, ver `store/routes.js`). Aquí soportamos ambos formatos
// por defensividad.
export async function fetchRecoleccionesContext({ fechas = [] } = {}) {
  const [routesRaw, ...ejecuciones] = await Promise.all([
    getRoutesWithDetails().catch(() => null),
    ...fechas.map((f) => getEjecucionDelDia(f).catch(() => EJECUCION_VACIA)),
  ]);
  const routes = Array.isArray(routesRaw)
    ? routesRaw
    : Array.isArray(routesRaw?.data)
    ? routesRaw.data
    : [];
  const ejecucionPorFecha = new Map();
  fechas.forEach((f, i) => ejecucionPorFecha.set(f, ejecuciones[i] ?? EJECUCION_VACIA));
  return { routes, ejecucionPorFecha };
}

// Clave de una parada = ruta + comercio + dirección. La dirección forma parte de la clave
// porque el mismo comercio puede ser DOS paradas distintas de la misma ruta.
export function paradaSlotKey(routeId, clientId, shippingAddressId) {
  return `${routeId}::${clientId ?? ""}::${shippingAddressId ?? ""}`;
}

function push(map, key, value) {
  if (!map.has(key)) map.set(key, []);
  map.get(key).push(value);
}

// Adapta un retiro del endpoint a la forma que consume `RetiroCell` (#correlativo, hora
// y PDF), que es la misma que tenía el pickup crudo.
function aRetiro(p) {
  return {
    _id: p.pickupId,
    correlative: p.correlative,
    pdfUrl: p.pdfUrl,
    date: p.ejecutadoEn,
    estado: p.estado,
    registradoPor: p.registradoPor,
  };
}

// Indexa la ejecución del día por parada. El emparejamiento ya viene resuelto por id
// desde el backend: acá no se adivina nada.
//
// Devuelve tres conjuntos, y ninguno se pisa con otro:
//  • `porParada`   → retiros atados a su parada.
//  • `recepciones` → lo que el comercio llevó a la bodega. No es de ninguna ruta.
//  • `sinParada`   → retiros que traen ruta pero no parada. Pasa si al emitir el remito
//    el comercio no figuraba entre las paradas de la ruta, o si la parada no tenía una
//    dirección válida. NO se los cuelga de una parada al azar: eso es exactamente el bug
//    que esta pantalla tenía. Se muestran aparte para que no queden invisibles.
export function buildEjecucionIndex(ejecucion) {
  const porParada = new Map();
  const sinParada = [];

  for (const p of ejecucion?.paradas || []) {
    if (!p?.shippingAddressId) {
      sinParada.push({ ...aRetiro(p), rutaId: p.rutaId, comercioId: p.comercioId });
      continue;
    }
    push(porParada, paradaSlotKey(p.rutaId, p.comercioId, p.shippingAddressId), aRetiro(p));
  }

  // Anulaciones indexadas por la MISMA clave que las paradas. Una anulación sin
  // `shippingAddressId` (la hizo una app anterior al cambio) se indexa además por
  // ruta+comercio, para que igual matchee la parada del comercio en esa ruta.
  const anuladas = new Set();
  for (const a of ejecucion?.anuladas || []) {
    anuladas.add(paradaSlotKey(a.rutaId, a.comercioId, a.shippingAddressId));
    if (!a.shippingAddressId) anuladas.add(`${a.rutaId}::${a.comercioId ?? ""}`);
  }

  return {
    porParada,
    sinParada,
    recepciones: ejecucion?.recepciones || [],
    anuladas,
  };
}

// ¿El repartidor anuló esta parada hoy? Misma resolución de ids que `retirosDeParada`.
export function paradaAnulada(index, routeId, client) {
  if (!index?.anuladas?.size) return false;
  const clientId = client?.id || client?._id || "";
  const addrId = client?.shippingAddress?.id || client?.shippingAddress?._id || "";
  return (
    index.anuladas.has(paradaSlotKey(routeId, clientId, addrId)) ||
    index.anuladas.has(`${routeId}::${clientId}`)
  );
}

// Retiros de una parada concreta de una ruta. `[]` si no se ejecutó.
export function retirosDeParada(index, routeId, client) {
  const clientId = client?.id || client?._id || "";
  const addrId = client?.shippingAddress?.id || client?.shippingAddress?._id || "";
  return index?.porParada.get(paradaSlotKey(routeId, clientId, addrId)) || [];
}

